import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Medal, Dumbbell, Flame } from "lucide-react";

/**
 * Gym-themed level-clear celebration. Replaces confetti with:
 *   - A central pulsing medal/trophy that scales in
 *   - Flying dumbbells, kettlebells, calorie-flame icons that radiate outward
 *   - A "calorie burn" amber ring expanding from center
 *
 * Auto-removes after ~1.6s. Mount conditionally on win detection.
 */
const ICONS = [Dumbbell, Flame, Medal, Trophy];
const COLORS = ["#E8A365", "#CDE079", "#F6BA6B", "#FCD9A0", "#D17A2A"];

export default function WinCelebration({ active }) {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (!active) {
      setParticles([]);
      return;
    }
    // Spawn 24 particles in a radial burst.
    const N = 24;
    const ps = Array.from({ length: N }, (_, i) => {
      const angle = (i / N) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      const dist = 180 + Math.random() * 200;
      return {
        id: i,
        Icon: ICONS[i % ICONS.length],
        color: COLORS[i % COLORS.length],
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist - 60, // slight upward bias
        size: 22 + Math.floor(Math.random() * 18),
        rot: Math.random() * 360 - 180,
        delay: Math.random() * 0.18,
      };
    });
    setParticles(ps);
  }, [active]);

  return (
    <AnimatePresence>
      {active && (
        <div
          data-testid="win-celebration"
          className="fixed inset-0 z-[150] pointer-events-none flex items-center justify-center"
          aria-hidden="true"
        >
          {/* Calorie-burn ring */}
          <motion.div
            initial={{ scale: 0, opacity: 0.85 }}
            animate={{ scale: 6, opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className="absolute rounded-full"
            style={{
              width: 120,
              height: 120,
              border: "6px solid #E8A365",
              boxShadow: "0 0 60px rgba(232,163,101,0.6)",
            }}
          />
          {/* Inner accent ring */}
          <motion.div
            initial={{ scale: 0, opacity: 1 }}
            animate={{ scale: 4, opacity: 0 }}
            transition={{ duration: 1.1, ease: "easeOut", delay: 0.1 }}
            className="absolute rounded-full"
            style={{
              width: 80,
              height: 80,
              border: "4px solid #CDE079",
            }}
          />

          {/* Central pulsing medal */}
          <motion.div
            initial={{ scale: 0, rotate: -30, opacity: 0 }}
            animate={{
              scale: [0, 1.4, 1.1, 1.2, 0.95],
              rotate: [-30, 8, -4, 2, 0],
              opacity: [0, 1, 1, 1, 0],
            }}
            transition={{ duration: 1.6, times: [0, 0.25, 0.5, 0.7, 1] }}
            className="relative flex items-center justify-center rounded-full"
            style={{
              width: 110,
              height: 110,
              background: "radial-gradient(circle at 35% 30%, #FCD9A0 0%, #E8A365 55%, #B97A2F 100%)",
              boxShadow: "0 12px 40px rgba(232,163,101,0.7), inset 0 -6px 12px rgba(0,0,0,0.25), inset 0 4px 8px rgba(255,255,255,0.5)",
              border: "3px solid #FCD9A0",
            }}
          >
            <Medal size={56} strokeWidth={2} color="#4D2A12" />
          </motion.div>

          {/* Radiating fitness particles */}
          {particles.map((p) => {
            const Icon = p.Icon;
            return (
              <motion.div
                key={p.id}
                initial={{ x: 0, y: 0, opacity: 0, scale: 0.4, rotate: 0 }}
                animate={{
                  x: p.dx,
                  y: p.dy,
                  opacity: [0, 1, 1, 0],
                  scale: [0.4, 1.2, 1, 0.6],
                  rotate: p.rot,
                }}
                transition={{
                  duration: 1.4,
                  delay: p.delay,
                  ease: "easeOut",
                  times: [0, 0.2, 0.7, 1],
                }}
                className="absolute"
                style={{
                  filter: `drop-shadow(0 2px 6px ${p.color}90)`,
                }}
              >
                <Icon size={p.size} strokeWidth={2.2} color={p.color} fill={p.color} fillOpacity={0.18} />
              </motion.div>
            );
          })}
        </div>
      )}
    </AnimatePresence>
  );
}
